import { enTetesDeLaPorte, valeursFermeesParLesVerrous } from '@taki/data-model';
import type { Condition, EnTeteDeFiche, EnTeteDeFicheDeclare } from '@taki/data-model';
import { cheminDeStockage, refuser } from '@taki/ui';
import type { Cell, ChampDeclare, EtapesDuGabarit, Option, ViewBinding } from '@taki/ui';
import type { FicheGenerique } from './app-declaree.js';

/**
 * LES EN-TETES DE LA RANGEE 3 D'UNE FICHE (chantier `fiche-suivi`,
 * `docs/socle/fiche-suivi.md`, section 2) : `porte.etapes` de
 * `taki.screens.json` dit QUELS champs liste, dans quel ordre et chacun dans
 * quel MODE (etapes ou selection) ; ce module les met dans la forme du gabarit
 * (une liste d'`EtapesDuGabarit`). L'ancienne forme, un objet seul, se lit
 * comme une liste d'un (`enTetesDeLaPorte`, la seule lecture de la clef).
 * Chaque en-tete se resout seul, exactement comme le champ unique d'avant.
 *
 * IL NE REINVENTE RIEN : les valeurs sont les options du catalogue tel que la
 * fiche le lit (la colonne du binding de `core.fiche`, ou le role `status`
 * quand le champ n'est que lui), dans leur ordre et avec leurs couleurs ; la
 * valeur se lit par le meme accesseur, et un clic s'ECRIT par le meme chemin
 * que l'edition en place de la fiche (`patchColumn`, ou `patchRecord` sur le
 * role) : optimisme, annulation, `saveState`, tout suit.
 *
 * LA VALEUR COCHEE EST CELLE QUE L'ECRAN MONTRE, ecriture en vol comprise
 * (recette du 27/10) : lue sur la fiche brute, la case ne suivait qu'apres la
 * relecture (2 a 5 s), et une fleche jouee entre-temps partait de l'ancienne
 * etape, donc ecrivait une valeur fausse. Elle passe par le sac optimiste de
 * l'edition en place (`valeurEnCours` de `useScreen`) : la valeur choisie se
 * coche aussitot, la fleche suivante part d'elle, la fiche relue la rend a la
 * base, un echec y ramene. Aucun etat a part.
 *
 * LES VERROUS D'UN EN-TETE (chantier `etapes-verrou`, `docs/socle/etapes-verrou.md`) :
 * quand la condition de l'un d'eux est vraie sur la fiche, les valeurs de rang
 * inferieur a `avant`, ou a partir de `depuis`, dans le catalogue ordonne (toute
 * la piste, sans l'un ni l'autre) ne se choisissent plus, et la raison du
 * premier verrou qui la ferme se dit au cadenas (G16). La valeur courante n'est
 * jamais verrouillee, elle reste visible. La condition lit la fiche comme une tranche,
 * sauf pour les champs que la rangee montre elle-meme : leur valeur est celle
 * de l'ecran, ecriture en vol comprise, pour qu'un clic qui ferme le verrou le
 * ferme aussitot. Un verrou tombe des que la fiche change (un bouton ailleurs
 * remet l'etape) : rien n'est retenu.
 */

/** La raison d'une piste qu'on ne peut pas jouer parce que le champ ne s'ecrit pas (G16). */
export const RAISON_ETAPES_EN_LECTURE = 'Ce champ ne se modifie pas sur cette porte.';

export interface EntreeDesEtapes {
  /**
   * `porte.etapes` telle que la porte la declare : la liste, l'ancien objet
   * seul, ou rien.
   */
  etapes: readonly EnTeteDeFicheDeclare[] | EnTeteDeFicheDeclare | undefined;
  /** Le binding de `core.fiche` de la porte : ses colonnes et ses roles. */
  binding: ViewBinding<FicheGenerique> | undefined;
  /** Les champs declares du jeu de la porte (pour le chemin de stockage). */
  champsDuJeu: readonly ChampDeclare[];
  /** La fiche ouverte, brute ; absente : pas de piste. */
  fiche: FicheGenerique | null;
  /** Son id ; `null`, un brouillon, un modele : pas de piste. */
  ficheId: string | null;
  /** La porte ne s'ecrit pas, et pourquoi (`useScreen().raisonAcces`). */
  raisonAcces: string | null;
  patchColumn(id: string, key: string, value: Cell): Promise<void>;
  patchRecord(id: string, role: string, value: Cell): Promise<void>;
  /**
   * La valeur de la case ecriture optimiste comprise (`useScreen().valeurEnCours`).
   * Absente : la valeur de la fiche, telle quelle.
   */
  valeurEnCours?(kind: 'role' | 'column', id: string, key: string, stockee: Cell): Cell;
  /**
   * Evalue la condition d'un verrou sur la fiche ouverte (`assemble.evaluer`).
   * `surcharge` donne la valeur, vue a l'ecran, d'un champ de la rangee 3 ; absent
   * ou `undefined` : la fiche. Absente, aucun verrou ne se ferme.
   */
  evaluer?(condition: Condition, surcharge: (nom: string) => unknown): boolean;
}

/** Un en-tete resolu sur le binding : son libelle, ses options (ordre du catalogue, archivees comprises), sa valeur, son ecriture. */
interface SourceDEnTete {
  libelle: string;
  options: readonly Option[];
  valeur: string | null;
  ecrit: boolean;
  ecrire(id: string): Promise<void>;
  parLeStatut: boolean;
}

/** La valeur courante, en id d'option : une liste vide, un vide, rien. */
function valeurDe(brute: Cell | undefined): string | null {
  if (brute === null || brute === undefined || brute === '') return null;
  return String(brute);
}

/**
 * LES EN-TETES DE LA FICHE OUVERTE, dans l'ordre de la porte ; `[]` sans
 * fiche (brouillon, modele) ou sans en-tete. Un en-tete dont le champ est
 * introuvable dans le binding ne se rend pas, les autres si.
 */
export function etapesDeLaFiche(entree: EntreeDesEtapes): EtapesDuGabarit[] {
  const enTetes = enTetesDeLaPorte({ etapes: entree.etapes });
  const sources = enTetes.map((enTete) => ({ enTete, source: sourceDeLEnTete(enTete, entree) }));
  /* LA VALEUR VUE A L'ECRAN DE CHAQUE EN-TETE, pour la condition d'un verrou. */
  const vues = new Map<string, string | null>();
  for (const { enTete, source } of sources) if (source !== null) vues.set(enTete.champ, source.valeur);
  const surcharge = (nom: string): unknown => (vues.has(nom) ? vues.get(nom) : undefined);
  return sources.flatMap(({ enTete, source }) => {
    const piste = source === null ? null : enTeteDeLaFiche(enTete, source, entree, surcharge);
    return piste === null ? [] : [piste];
  });
}

/**
 * LA SOURCE D'UN EN-TETE, ou `null` (pas de fiche, champ introuvable dans le
 * binding).
 */
function sourceDeLEnTete(etapes: EnTeteDeFiche, entree: EntreeDesEtapes): SourceDEnTete | null {
  const { binding, fiche, ficheId } = entree;
  if (binding === undefined || fiche === null || ficheId === null) return null;
  const nu = etapes.champ.startsWith('socle:') ? etapes.champ.slice('socle:'.length) : etapes.champ;
  const chemin = cheminDeStockage(etapes.champ, entree.champsDuJeu);
  const colonne = [...(binding.fieldColumns ?? []), ...(binding.columns ?? [])].find((candidate) => candidate.key === nu);
  const statut = binding.roles['status'];
  const parLeStatut = statut !== undefined && statut.path === chemin;
  const enCours = (kind: 'role' | 'column', key: string, stockee: Cell | undefined): string | null =>
    valeurDe(entree.valeurEnCours === undefined ? stockee : entree.valeurEnCours(kind, ficheId, key, stockee ?? null));
  if (colonne !== undefined) {
    return {
      libelle: colonne.label,
      options: colonne.optionsPour?.(fiche) ?? colonne.options?.() ?? [],
      valeur: enCours('column', colonne.key, colonne.value(fiche)),
      ecrit: colonne.set !== undefined,
      ecrire: (id) => entree.patchColumn(ficheId, colonne.key, id),
      parLeStatut: false,
    };
  }
  if (parLeStatut) {
    return {
      libelle: statut.label ?? nu,
      options: statut.options?.() ?? [],
      valeur: enCours('role', 'status', statut.get(fiche)),
      ecrit: statut.set !== undefined,
      ecrire: (id) => entree.patchRecord(ficheId, 'status', id),
      parLeStatut: true,
    };
  }
  return null;
}

/**
 * LA PISTE D'UN EN-TETE. Une option ARCHIVEE ne se propose plus, sauf si la
 * fiche la porte encore : la piste dit la verite de la fiche.
 */
function enTeteDeLaFiche(
  etapes: EnTeteDeFiche,
  source: SourceDEnTete,
  entree: EntreeDesEtapes,
  surcharge: (nom: string) => unknown,
): EtapesDuGabarit {
  const valeurs = source.options
    .filter((option) => option.archived !== true || option.value === source.valeur)
    .map((option) => ({ id: option.value, libelle: option.label, couleur: option.tone }));
  const raison = entree.raisonAcces ?? (source.ecrit ? null : RAISON_ETAPES_EN_LECTURE);
  const verrou = raison === null ? verrousDeLEnTete(etapes, source, valeurs, entree, surcharge) : undefined;
  return {
    champ: etapes.champ,
    libelle: source.libelle,
    mode: etapes.mode,
    valeurs,
    valeur: source.valeur,
    ...(raison === null
      ? {
          onChoisir: (id: string) => {
            if (id === source.valeur) return;
            /* Le verrou refuse AUSSI au geste, pas seulement a l'affichage : rien ne s'ecrit. */
            if (verrou?.ids.includes(id) === true) return;
            void source.ecrire(id);
          },
        }
      : { raison }),
    ...(verrou === undefined ? {} : { verrou }),
    ...(source.parLeStatut ? { remplaceLaPastille: true } : {}),
  };
}

/**
 * LES VALEURS QUE LES VERROUS FERMENT, et pourquoi, ou `undefined` : pas de
 * verrou, aucune condition vraie, ou rien de ferme. Le rang est celui du
 * catalogue ordonne du champ, archivees comprises ; un `avant` ou un `depuis`
 * que le catalogue ne connait plus ne ferme rien. La valeur courante n'est
 * jamais fermee. Une valeur est fermee des qu'UN verrou actif la ferme, avec la
 * raison du premier qui la ferme (`valeursFermeesParLesVerrous`, la regle que
 * le kanban lit aussi). `raison` : celle du premier verrou, pour un repli.
 */
function verrousDeLEnTete(
  etapes: EnTeteDeFiche,
  source: SourceDEnTete,
  valeurs: readonly { id: string }[],
  entree: EntreeDesEtapes,
  surcharge: (nom: string) => unknown,
): { ids: string[]; raison: string; raisons: Record<string, string> } | undefined {
  const { evaluer } = entree;
  if (etapes.verrous === undefined || evaluer === undefined) return undefined;
  const fermees = valeursFermeesParLesVerrous(
    etapes.verrous,
    source.options.map((option) => option.value),
    source.valeur,
    (verrou) => evaluer(verrou.si, surcharge),
  );
  const ids = valeurs.map((v) => v.id).filter((id) => fermees.has(id));
  if (ids.length === 0) return undefined;
  return { ids, raison: fermees.get(ids[0]!)!, raisons: Object.fromEntries(ids.map((id) => [id, fermees.get(id)!])) };
}

/**
 * LE KANBAN RESPECTE LES VERROUS (chantier `etapes-verrou`) : une carte qu'on
 * depose dans une colonne FERMEE par un verrou de l'en-tete de sa porte qui
 * porte le meme champ que son `groupKey` n'ecrit rien. Le deplacement est un
 * refus d'ecriture du socle (`refuser`, `views/refus.ts`) : la carte revient
 * dans sa colonne, rien n'est ecrit, et la pastille de la rangee 3 dit la
 * raison, sans « Reessayer ». La condition se lit sur la CARTE deplacee.
 *
 * Meme regle que la barre d'etapes (`valeursFermeesParLesVerrous`) : le
 * catalogue ordonne est celui du `groupKey`, la colonne de la carte (sa valeur
 * courante) n'est jamais fermee. Le refus est dans le `set` du role, donc il
 * vaut pour toute ecriture de ce role, pas seulement pour le glisser.
 *
 * Rend le MEME tableau quand rien ne se verrouille (la projection ne repart
 * pas pour rien) ; sinon une copie, le binding du kanban seul reecrit.
 */
export function bindingsSousVerrous(
  bindings: ViewBinding<FicheGenerique>[],
  entree: {
    etapes: EntreeDesEtapes['etapes'];
    champsDuJeu: readonly ChampDeclare[];
    evaluer: (condition: Condition, fiche: FicheGenerique) => boolean;
  },
): ViewBinding<FicheGenerique>[] {
  const enTetes = enTetesDeLaPorte({ etapes: entree.etapes }).filter((enTete) => enTete.verrous !== undefined);
  if (enTetes.length === 0) return bindings;
  let change = false;
  const sous = bindings.map((binding) => {
    const groupe = binding.type === 'core.kanban' ? binding.roles['groupKey'] : undefined;
    if (groupe?.set === undefined || groupe.path === undefined) return binding;
    const enTete = enTetes.find((candidat) => cheminDeStockage(candidat.champ, entree.champsDuJeu) === groupe.path);
    if (enTete?.verrous === undefined) return binding;
    const { verrous } = enTete;
    const ecrire = groupe.set.bind(groupe);
    change = true;
    return {
      ...binding,
      roles: {
        ...binding.roles,
        groupKey: {
          ...groupe,
          set: (fiche: FicheGenerique, valeur: Cell): Promise<void> => {
            const courante = valeurDe(groupe.get(fiche));
            const fermees = valeursFermeesParLesVerrous(
              verrous,
              (groupe.options?.() ?? []).map((option) => option.value),
              courante,
              (verrou) => entree.evaluer(verrou.si, fiche),
            );
            const raison = fermees.get(String(valeur ?? ''));
            if (raison !== undefined) refuser(raison);
            return ecrire(fiche, valeur);
          },
        },
      },
    };
  });
  return change ? sous : bindings;
}
